import { FetchResult } from '@apollo/client';
import * as Sentry from '@sentry/react';
import isChromatic from 'chromatic';
import { printSchema } from 'graphql';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSchemaFromEndpointIntrospection } from 'src/app/graph/explorerPage/hooks/useExplorerState/useSchema/useSchemaFromEndpointIntrospection/useSchemaFromEndpointIntrospection';
import { useCreateSupergraph } from 'src/hooks/useCreateSupergraph';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useIsCloudEndpointInitiating } from 'src/hooks/useIsCloudEndpointInitiating';
import { useUpdateVariantReadme } from 'src/hooks/useUpdateVariantReadme';
import { useUpsertRouterConfig } from 'src/hooks/useUpsertRouterConfig';
import { generateUniqueString } from 'src/lib/generateUniqueString';
import { DEFAULT_VARIANT_NAME } from 'src/lib/graphOnboarding/constants';
import { generateGraphId } from 'src/lib/graphOnboarding/helpers/utils';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  GRAPH_TITLE,
  PLAYBACK_SUBGRAPH_URI,
  README,
  ROUTER_CONFIG,
  SPOTIFY_SUBGRAPH_URI,
} from './constants';

export const DemoGraphContext = React.createContext<{
  createDemoSupergraph:
    | (() => Promise<
        | FetchResult<
            GraphQLTypes.CreateSupergraph,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            Record<string, any>,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            Record<string, any>
          >
        | undefined
      >)
    | null;
  orgId: string;
  graphVariant: string;
  isProcessing: boolean;
  hasSchemaDocs: boolean;
}>({
  orgId: '',
  createDemoSupergraph: null,
  isProcessing: false,
  graphVariant: DEFAULT_VARIANT_NAME,
  hasSchemaDocs: false,
});

interface DemoGraphProviderProps {
  children: React.ReactNode;
}

export const DemoGraphProvider = ({ children }: DemoGraphProviderProps) => {
  const [orgId] = useCurrentAccountId();
  const [graphId, setGraphId] = useState('');
  const [hasRouterConfigBeenUpdated, setHasRouterConfigBeenUpdated] =
    useState(false);
  const { createSupergraph, createSupergraphLoading } = useCreateSupergraph();
  const { upsertRouterConfig, upsertRouterLoading } = useUpsertRouterConfig();
  const { updateVariantReadme, updateVariantReadmeLoading } =
    useUpdateVariantReadme();
  const {
    routerIsInitiating,
    routerStatus,
    loading: isRouterIsInitiatingLoading,
  } = useIsCloudEndpointInitiating({
    graphRef: graphId
      ? {
          graphId,
          graphVariant: DEFAULT_VARIANT_NAME,
        }
      : null,
  });

  // Don't introspect subgraphs in chromatic
  const skipIntrospection = isChromatic();
  const {
    schema: playbackSchemaFromIntrospection,
    subgraphSdl: playbackSubgraphSdlFromIntrospection,
    introspectionFailureType: playbackSubgraphSdlIntrospectionFailureType,
    introspectionResponseErrors: playbackSubgraphSdlIntrospectionResponseErrors,
  } = useSchemaFromEndpointIntrospection({
    includeCookies: 'cycle-on-unreachable',
    uri: PLAYBACK_SUBGRAPH_URI,
    skipPolling: true,
    pollForSubgraphSdlFirst: true,
    skip: skipIntrospection,
    updateOnConfigChange: true,
  });
  const {
    schema: spotifySchemaFromIntrospection,
    subgraphSdl: spotifySubgraphSdlFromIntrospection,
    introspectionFailureType: spotifySubgraphSdlIntrospectionFailureType,
    introspectionResponseErrors: spotifySubgraphSdlIntrospectionResponseErrors,
  } = useSchemaFromEndpointIntrospection({
    includeCookies: 'cycle-on-unreachable',
    uri: SPOTIFY_SUBGRAPH_URI,
    skipPolling: true,
    pollForSubgraphSdlFirst: true,
    skip: skipIntrospection,
    updateOnConfigChange: true,
  });

  const playbackSchemaDoc = useMemo(
    () =>
      playbackSubgraphSdlFromIntrospection ??
      (playbackSchemaFromIntrospection
        ? printSchema(playbackSchemaFromIntrospection)
        : undefined),
    [playbackSchemaFromIntrospection, playbackSubgraphSdlFromIntrospection],
  );

  const spotifySchemaDoc = useMemo(
    () =>
      spotifySubgraphSdlFromIntrospection ??
      (spotifySchemaFromIntrospection
        ? printSchema(spotifySchemaFromIntrospection)
        : undefined),
    [spotifySchemaFromIntrospection, spotifySubgraphSdlFromIntrospection],
  );

  const createDemoSupergraph = useCallback(async () => {
    if (!orgId || !spotifySchemaDoc || !playbackSchemaDoc) return;
    const newGraphId = generateGraphId(GRAPH_TITLE, generateUniqueString());
    const result = await createSupergraph({
      accountId: orgId,
      graphType: GraphQLTypes.GraphType.CLOUD_SUPERGRAPH,
      hiddenFromUninvitedNonAdmin: false,
      graphId: newGraphId,
      title: GRAPH_TITLE,
      variantCreationConfig: {
        variantName: DEFAULT_VARIANT_NAME,
        buildConfigInput: {
          apiDirectivePassThrough: [],
          buildPipelineTrack: null,
          composition: {
            subgraphs: [
              {
                document: spotifySchemaDoc,
                name: 'spotify',
                routingURL: SPOTIFY_SUBGRAPH_URI,
              },
              {
                document: playbackSchemaDoc,
                name: 'playback',
                routingURL: PLAYBACK_SUBGRAPH_URI,
              },
            ],
          },
        },
      },
    });

    if (
      !result.errors &&
      result.data?.account?.createGraph?.__typename !== 'GraphCreationError' &&
      result?.data?.account?.createGraph?.id
    ) {
      setGraphId(newGraphId);
    }

    return result;
  }, [createSupergraph, orgId, playbackSchemaDoc, spotifySchemaDoc]);

  const isProcessing =
    createSupergraphLoading ||
    isRouterIsInitiatingLoading ||
    routerIsInitiating ||
    updateVariantReadmeLoading;

  const updateRouterConfig = useCallback(async () => {
    try {
      const result = await upsertRouterConfig({
        graphId,
        variant: DEFAULT_VARIANT_NAME,
        newRouterConfig: ROUTER_CONFIG(graphId),
      });

      return result;
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [graphId, upsertRouterConfig]);

  const updateGraphReadme = useCallback(async () => {
    try {
      const result = await updateVariantReadme({
        graphId,
        variant: DEFAULT_VARIANT_NAME,
        readme: README,
      });

      return result;
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [graphId, updateVariantReadme]);

  /*
    TODO: there is some edge-case weirdness where if creating multiple demo graphs at once, 
    we don't get an updated `routerStatus` for the newest graph which makes it extremely 
    challenging to know when it is ready to trigger the router config update.  We need to
    revisit this if it becomes an issue for customers.
  */
  useEffect(() => {
    if (
      routerStatus !== GraphQLTypes.RouterStatus.RUNNING &&
      hasRouterConfigBeenUpdated
    ) {
      setHasRouterConfigBeenUpdated(false);
    } else if (
      !hasRouterConfigBeenUpdated &&
      routerStatus === GraphQLTypes.RouterStatus.RUNNING
    ) {
      setHasRouterConfigBeenUpdated(true);
      updateRouterConfig();
      updateGraphReadme();
    }
  }, [
    hasRouterConfigBeenUpdated,
    routerStatus,
    updateRouterConfig,
    updateGraphReadme,
  ]);

  // Report on playback subgraph introspection errors
  useEffect(() => {
    if (playbackSubgraphSdlIntrospectionResponseErrors?.length) {
      const err = new Error(
        `Failed introspection for Spotify Demo subgraph 'playback' with following errors: ${playbackSubgraphSdlIntrospectionResponseErrors.join(
          ' ',
        )}. Failure type: ${playbackSubgraphSdlIntrospectionFailureType}`,
      );
      Sentry.captureException(err);
    }
  }, [
    playbackSubgraphSdlIntrospectionFailureType,
    playbackSubgraphSdlIntrospectionResponseErrors,
  ]);

  // Report on spotify subgraph introspection errors
  useEffect(() => {
    if (spotifySubgraphSdlIntrospectionResponseErrors?.length) {
      const err = new Error(
        `Failed introspection for Spotify Demo subgraph 'spotify' with following errors: ${spotifySubgraphSdlIntrospectionResponseErrors.join(
          ' ',
        )}. Failure type: ${spotifySubgraphSdlIntrospectionFailureType}`,
      );
      Sentry.captureException(err);
    }
  }, [
    spotifySubgraphSdlIntrospectionFailureType,
    spotifySubgraphSdlIntrospectionResponseErrors,
  ]);

  const contextValue = useMemo(
    () => ({
      createDemoSupergraph,
      orgId: orgId || '',
      graphVariant: DEFAULT_VARIANT_NAME,
      isProcessing: isProcessing || upsertRouterLoading,
      hasSchemaDocs: !!playbackSchemaDoc && !!spotifySchemaDoc,
    }),
    [
      createDemoSupergraph,
      orgId,
      isProcessing,
      upsertRouterLoading,
      playbackSchemaDoc,
      spotifySchemaDoc,
    ],
  );

  return (
    <DemoGraphContext.Provider value={contextValue}>
      {children}
    </DemoGraphContext.Provider>
  );
};

export const useDemoGraphContext = () => {
  const context = React.useContext(DemoGraphContext);
  if (!context) {
    throw new Error(
      'useDemoGraphContext must be used within a DemoGraphProvider',
    );
  }
  return context;
};
